<script>
import appConfig from '@src/app.config.json'
import Layout from '@layouts/user-management.vue'
import axios from 'axios'
import { hasErrors } from '@utils/form'
import { permissionsService } from '@utils/permissions'
import { httpServices } from '@utils/http-services'
import { services } from '@utils/services'
import { formatPhoneNumber } from '@utils/phone-format'

export default {
  page: {
    title: 'Connection Profile',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout },
  data() {
    return {
      hasErrors,
      connection: null,
      connections: [],
      figmentIsUsing: false,
      sims: [],
      activeSims: [],
      inactiveSims: [],
      orgs: [],
      title: 'Do you want to update this locked connection?',
      content: `By clicking the OK button you may affect another user's workflow.`,
      visible: false,
      confirmLoading: false,
      permissionsService: permissionsService,
      formLayout: 'horizontal',
      hardwareIds: [],
      form: this.$form.createForm(this, { name: 'coordinated' }),
    }
  },
  mounted() {
    httpServices
      .getHardwares()
      .then((res) => {
        this.hardwareIds = res.map((i) => i.meta.id)
      })
      .then(() => {
        this.getSims()
      })
      .then(() => {
        httpServices.getFigments().then((res) => {
          const figments = res
          const sims = figments.map((f) => f.meta.sims.pk)
          if (sims.includes(this.connection?.meta?.sim)) {
            this.figmentIsUsing = true
            this.$notification.warning({
              message: `Figment is associated to a sim being used by this connection. Please change the figment before editing this connection.`,
            })
          }
        })
      })
  },
  methods: {
    getSims() {
      axios
        .get(
          process.env.VUE_APP_KONSUL_API_URL +
            '/connections/' +
            this.$route.params.connectionId,
          {
            headers: {
              Authorization: 'Bearer ' + this.$auth.user.access_token,
            },
          }
        )
        .then((res) => {
          this.connection = res.data
          httpServices.getSims().then((res) => {
            this.sims = res.filter(
              (i) => i.hardwareId === this.connection.meta.hardwareId
            )
            this.sims.push({ pk: 'N/A', meta: { imsi: 'N/A' } })

            httpServices.getConnections().then((res) => {
              this.connections = res
              this.buildWan()
            })
          })
          this.$nextTick(() => {
            this.form.validateFields()
          })
        })
    },
    buildWan() {
      const arr = []
      this.connections.forEach((conn) => {
        const sim = this.sims.filter((i) => i?.pk === conn?.meta?.sim)[0]
        if (sim) {
          arr.push(sim)
        }
      })

      // filter by assigned user sim and assigned user group
      if (this.$auth.user.profile.permissions.role !== 'admin') {
        httpServices.getGroups().then((res) => {
          this.activeSims = services.filterSims(
            arr,
            this.sims,
            res,
            this.$auth.user.email
          )
        })
      } else {
        // admin sees all sims
        this.activeSims = arr
      }

      this.activeSims = this.activeSims.filter((i) => i.pk !== 'N/A')

      // display inactive sims
      this.inactiveSims = this.sims.filter(
        (i) => !this?.activeSims?.some((e) => e.pk === i.pk)
      )
    },
    showModal() {
      if (this.connection.meta.locked) {
        this.visible = true
      } else {
        this.handleSubmit()
      }
    },
    formatSIM(s) {
      const mtn = formatPhoneNumber(s?.meta?.mtn) || 'N/A'
      const imsi = s?.meta?.imsi || 'N/A'
      return `${mtn} · ${imsi}`
    },
    handleOk(e) {
      this.confirmLoading = true
      this.handleSubmit()
    },
    handleCancel(e) {
      this.visible = false
    },
    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          const baselineSim = JSON.parse(
            JSON.stringify(this.connection.meta.sim)
          )

          this.connection.meta = {
            ...this.connection.meta,
            id: values.id,
            hardwareId: values.hardwareId,
            locked: values.locked,
            sim: values.sim,
            loading: values.loading,
          }

          axios
            .put(
              process.env.VUE_APP_KONSUL_API_URL + '/connections',
              this.connection,
              {
                headers: {
                  Authorization: 'Bearer ' + this.$auth.user.access_token,
                },
              }
            )
            .then(
              (_res) => {
                if (baselineSim !== values.sim) {
                  const slotId = this.sims
                    .filter((i) => i.pk === this.connection.meta.sim)
                    .map((j) => j.meta.simSlot)[0]
                  const connId = this.connection.meta.id
                  const options = {
                    method: 'PUT',
                    url:
                      process.env.VUE_APP_KONSUL_API_URL +
                      '/api/sms/link/update',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: 'Bearer ' + this.$auth.user.access_token,
                    },
                    data: {
                      hardwareId: values.hardwareId,
                      connectionId: connId?.toString(),
                      slotId: slotId?.toString(),
                    },
                  }
                  axios.request(options).then((_r) => {
                    this.$notification.warning({
                      message: `Changing connection`,
                    })
                  })
                  this.$router.push({
                    name: 'sim-management',
                  })
                } else {
                  this.$notification.success({
                    message: `Connection Updated.`,
                  })
                  this.$router.push({
                    name: 'sim-management',
                  })
                }
              },
              () => {
                this.$notification.error({
                  message:
                    'Failed to Update Connection - This SIM may be in use by another connection',
                })
                this.$router.push({
                  name: 'sim-management',
                })
              }
            )
        }
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="ant-row profile--container">
      <div class="breadcrumb--container">
        <a-breadcrumb>
          <a-breadcrumb-item href="#">
            <router-link to="/">
              <a-icon type="home" />
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item href="#">
            <router-link to="/sim-management">
              <a-icon type="phone" />
              <span>SIM Management</span>
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <a-icon type="profile" />
            Connection Profile
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>
      <div class="form-container">
        <div
          class="header"
          style="
            padding: 1.2rem;
            margin-bottom: 0;
            text-align: center;
            background: #141414;
            border-radius: 4px 4px 0 0;
          "
        >
          <h4 style="margin: 0; font-weight: 100; color: #fff">
            <a-icon type="profile" style="padding-right: 4px" />
            Connection Profile
            <span v-if="connection"
              >&middot;
              {{ connection.meta.hardwareId + '-' + connection.meta.id }}</span
            >
          </h4>
        </div>

        <template v-if="connection">
          <a-modal
            title="Title"
            :visible="visible"
            :confirm-loading="confirmLoading"
            @ok="handleOk"
            @cancel="handleCancel"
          >
            <h4>{{ title }}</h4>
            <p>{{ content }}</p>
          </a-modal>

          <a-form :form="form" @submit.prevent="showModal">
            <a-card>
              <a-tabs default-active-key="1">
                <a-tab-pane key="1" tab="Connection Meta Data">
                  <a-card style="width: 100%">
                    <a-row>
                      <a-col :sm="12" :md="12">
                        <a-form-item label="Connection ID">
                          <a-input-number
                            v-decorator="[
                              'id',
                              {
                                initialValue: parseInt(connection.meta.id),
                                rules: [
                                  {
                                    required: true,
                                  },
                                  {
                                    type: 'number',
                                  },
                                ],
                              },
                            ]"
                            :disabled="true"
                            :min="1"
                            :max="1000"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="12" :md="12">
                        <a-form-item label="Select a Hardware Id">
                          <a-select
                            v-decorator="[
                              'hardwareId',
                              {
                                initialValue: connection.meta.hardwareId,
                                rules: [
                                  {
                                    required: true,
                                  },
                                ],
                              },
                            ]"
                          >
                            <a-select-option
                              v-for="(h, i) in hardwareIds"
                              :key="h + i"
                              :value="h"
                            >
                              {{ h.toUpperCase() }}
                            </a-select-option>
                          </a-select>
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="12">
                        <a-form-item label="Connection Locked for SMS">
                          <a-switch
                            v-decorator="[
                              `locked`,
                              {
                                initialValue: connection.meta.locked,
                                valuePropName: 'checked',
                              },
                            ]"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="12">
                        <a-form-item label="Connection Loading">
                          <a-switch
                            v-decorator="[
                              `loading`,
                              {
                                initialValue: connection.meta.loading,
                                valuePropName: 'checked',
                              },
                            ]"
                            :disabled="!permissionsService.isVanishedAdmin()"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="8">
                        <a-form-item label="SIMs">
                          <a-select
                            v-decorator="[
                              `sim`,
                              {
                                initialValue: connection.meta.sim,
                              },
                            ]"
                          >
                            <a-select-opt-group>
                              <span slot="label"
                                ><a-icon type="disconnect" /> Inactive</span
                              >

                              <a-select-option
                                v-for="(s, i) in inactiveSims"
                                :key="i + 'sim'"
                                :value="s.pk"
                              >
                                <span>
                                  {{ formatSIM(s) }}
                                </span>
                              </a-select-option>
                            </a-select-opt-group>

                            <a-select-opt-group>
                              <span slot="label"
                                ><a-icon type="check" /> Active</span
                              >

                              <a-select-option
                                v-for="(s, i) in activeSims"
                                :key="i + 'sim'"
                                :disabled="true"
                                :value="s.pk"
                              >
                                <span>
                                  {{ formatSIM(s) }}
                                </span>
                              </a-select-option>
                            </a-select-opt-group>
                          </a-select>
                        </a-form-item>
                      </a-col>
                    </a-row>
                  </a-card>
                </a-tab-pane>
              </a-tabs>

              <a-row class="submit--container">
                <a-col :sm="16" :md="8">
                  <a-form-item>
                    <a-button
                      type="primary"
                      html-type="submit"
                      :disabled="
                        hasErrors(form.getFieldsError()) || figmentIsUsing
                      "
                    >
                      Save
                    </a-button>
                  </a-form-item>
                </a-col>
                <a-col :sm="16" :md="8">
                  <a-form-item>
                    <a-button @click="$router.go(-1)"> Cancel </a-button>
                  </a-form-item>
                </a-col>
              </a-row>
            </a-card>
          </a-form>
        </template>
        <template v-else>
          <div class="loader--container">
            <a-icon
              type="loading"
              style="padding-left: 4px; font-size: 24px; color: #1890ff"
              spin
            />
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>
